import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, CaretDown, Check } from '@phosphor-icons/react';
import { languages } from '../i18n';

export const LanguageSelector = ({ variant = 'default' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const currentLang = languages[i18n.language] || languages.en;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('turfpulse_language', langCode);
    setIsOpen(false);
  };

  // Compact variant for mobile/header
  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted/50 transition-colors"
          aria-label="Select language"
          data-testid="language-selector-compact"
        >
          <span className="text-lg">{currentLang.flag}</span>
          <CaretDown 
            size={14} 
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>

        {isOpen && (
          <div className="fixed right-2 mt-1 w-44 bg-background border border-border rounded-lg shadow-lg overflow-hidden" style={{ zIndex: 9999 }}>
            {Object.entries(languages).map(([code, lang]) => (
              <button
                key={code}
                onClick={() => handleLanguageChange(code)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors text-left ${
                  i18n.language === code ? 'bg-muted/30' : ''
                }`}
                data-testid={`lang-option-${code}`}
              >
                <span className="text-lg">{lang.flag}</span>
                <span className="flex-1 text-sm">{lang.nativeName}</span>
                {i18n.language === code && (
                  <Check size={16} className="text-green-600" weight="bold" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default variant - full button with text
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:bg-muted/50 transition-colors"
        aria-label="Select language"
        data-testid="language-selector"
      >
        <Globe size={18} className="text-muted-foreground" />
        <span className="text-lg">{currentLang.flag}</span>
        <span className="text-sm font-medium">{currentLang.nativeName}</span>
        <CaretDown 
          size={14} 
          className={`transition-transform text-muted-foreground ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-52 bg-background border border-border rounded-lg shadow-lg z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
              Select Language
            </p>
          </div>
          {Object.entries(languages).map(([code, lang]) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors text-left ${
                i18n.language === code ? 'bg-muted/30' : ''
              }`}
              data-testid={`lang-option-${code}`}
            >
              <span className="text-xl">{lang.flag}</span>
              <div className="flex-1">
                <p className="text-sm font-medium">{lang.nativeName}</p>
                <p className="text-xs text-muted-foreground">{lang.name}</p>
              </div>
              {i18n.language === code && (
                <Check size={18} className="text-green-600" weight="bold" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
